import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import type { TDrawerItem, TDrawerList } from '../../types';
import { Link } from '@dx-ui/global-ui-prism';
import Icon from '@dx-ui/osc-icon';
import {
  dispatchGlobalNavigationClickBasedOnLabel,
  sendGlobalNavLinkClickGoal,
} from '../../header.utilities';
import cx from 'classnames';
import { useRouter } from 'next/router';
import { replaceLang } from '@dx-ui/framework-i18n';
import { HeaderLogoLink } from '../header-logo';
import { useHeaderContext } from '../../context/header-context';
import { useHeaderStateContext } from '../../context/header-state-context';

export const MobileNavDrawerList = () => {
  const {
    brand,
    visual: { suppressLogo },
    navigation: { menuItems, globalNavLinks, onGlobalNavigationClick },
  } = useHeaderContext();
  const {
    menuState: { isMenuOpen, setIsMenuOpen },
    styling: { logoVariant },
  } = useHeaderStateContext();
  const [nextItem, setNextItem] = useState<TDrawerItem | undefined>();
  const [nextItemIndex, setNextItemIndex] = useState<number>();
  const [selectedItem, setSelectedItem] = useState<TDrawerItem | undefined>();
  const [selectedItemIndex, setSelectedItemIndex] = useState<number>();
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [submenuLogoBrand, setSubmenuLogoBrand] = useState<TDrawerList['brand']>(brand);
  const lastSelectedButton = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation('osc-header');
  const { locale = 'en' } = useRouter();

  const onClose = () => setIsMenuOpen(false);

  useEffect(() => {
    if (!isMenuOpen) setSelectedItem(undefined);
  }, [isMenuOpen]);

  const goBack = () => {
    setIsFadingOut(true);
    setNextItem(undefined);
    setTimeout(() => {
      lastSelectedButton?.current?.focus();
    }, 350);
  };

  const focusOnBack = () => {
    setTimeout(() => {
      backButtonRef?.current?.focus();
    }, 50);
  };

  const itemSelected = (menuItem: TDrawerItem, index: number) => {
    setIsFadingOut(true);
    setNextItem(menuItem);
    setNextItemIndex(index);
  };

  return (
    <div
      className={cx('osc-nav-drawer', isFadingOut ? 'animate-fadeout opacity-0' : 'animate-fadein')}
      onAnimationEnd={() => {
        if (isFadingOut) {
          setIsFadingOut(false);
          setSelectedItem(nextItem);
          setSelectedItemIndex(nextItemIndex);
          focusOnBack();
        }
      }}
    >
      <div className="relative border-surface px-4 py-3">
        <button onClick={onClose} type="button" className="px-2" aria-expanded={isMenuOpen}>
          <Icon name="close" className="m-auto" />
          <span className="text--xs font-semibold brand-ey:font-normal brand-wa:font-normal">
            <span className="sr-only">{t('close')}</span>
            <span className="sr-only">{t('menu')}</span>
          </span>
        </button>
      </div>
      {selectedItem ? (
        <>
          {!suppressLogo && (
            <div className="px-4 pb-4 pt-2">
              <HeaderLogoLink brand={submenuLogoBrand} variant={logoVariant} />
            </div>
          )}
          <button
            ref={backButtonRef}
            onClick={goBack}
            className="osc-nav-main-link osc-nav-drawer-link-active flex h-12 w-full items-center border-s-8 border-b-surface border-s-surface-interactive pe-2 ps-4 text-left"
            aria-expanded="true"
            type="button"
          >
            <Icon name="arrowhead-small-right" className="me-3 rotate-180 rtl:rotate-0" />
            <span>{selectedItem.label}</span>
          </button>
        </>
      ) : null}

      {selectedItem ? (
        <ul>
          {selectedItem.subMenu?.map((menuItem, index) =>
            menuItem.link?.url ? (
              <li key={menuItem.label}>
                <Link
                  colorTheme="inherit"
                  anchorClassName="block osc-nav-drawer-link border-b-surface border-b"
                  underline={false}
                  className={cx('flex h-12 items-center ps-4', {
                    'border-t border-surface': index === 0,
                  })}
                  url={replaceLang(menuItem.link.url, locale)}
                  isNewWindow={menuItem.link.isNewWindow}
                  onClick={(event) => {
                    dispatchGlobalNavigationClickBasedOnLabel(
                      menuItem.label,
                      selectedItem.label,
                      onGlobalNavigationClick
                    );
                    menuItem.onClick?.();
                    menuItem.link?.onClick?.(event);
                  }}
                  data-conductrics-goal={menuItem.link.experimentationConfiguration?.goal}
                  data-conductrics-value={menuItem.link.experimentationConfiguration?.value}
                >
                  {menuItem.label}
                </Link>
              </li>
            ) : null
          )}
        </ul>
      ) : (
        // If no submenu is currently selected, show main set of menu items
        <ul>
          {menuItems?.length && !suppressLogo ? (
            <li className="px-4">
              <div>
                <HeaderLogoLink brand={brand} variant={logoVariant} />
              </div>
            </li>
          ) : null}
          {menuItems?.map((menuItem, index) => {
            if (menuItem?.subMenu?.length) {
              return (
                <li key={menuItem.label}>
                  <button
                    ref={selectedItemIndex === index ? lastSelectedButton : null}
                    onClick={() => {
                      setSubmenuLogoBrand(brand);
                      itemSelected(menuItem, index);
                      dispatchGlobalNavigationClickBasedOnLabel(
                        menuItem.label,
                        undefined,
                        onGlobalNavigationClick
                      );
                    }}
                    className={cx(
                      'osc-nav-main-link osc-nav-drawer-link flex h-12 w-full animate-fadein items-center justify-between border-b border-surface ps-4 text-left'
                    )}
                    aria-expanded="false"
                    type="button"
                    data-conductrics-goal={menuItem.link?.experimentationConfiguration?.goal}
                    data-conductrics-value={menuItem.link?.experimentationConfiguration?.value}
                  >
                    <span>{menuItem.label}</span>
                    <Icon name="arrowhead-small-right" className="me-3 ms-auto rtl:rotate-180" />
                  </button>
                </li>
              );
            } else if (menuItem.link?.url) {
              return (
                <li key={menuItem.label}>
                  <Link
                    colorTheme="inherit"
                    anchorClassName="osc-nav-drawer-link block"
                    underline={false}
                    className={cx(
                      'osc-nav-main-link flex h-12 items-center border-b border-surface ps-4'
                    )}
                    url={replaceLang(menuItem.link.url, locale)}
                    isNewWindow={menuItem.link.isNewWindow}
                    onClick={(event) => {
                      dispatchGlobalNavigationClickBasedOnLabel(
                        menuItem.label,
                        undefined,
                        onGlobalNavigationClick
                      );
                      menuItem.onClick?.();
                      menuItem.link?.onClick?.(event);
                      sendGlobalNavLinkClickGoal();
                    }}
                    data-conductrics-goal={menuItem.link.experimentationConfiguration?.goal}
                    data-conductrics-value={menuItem.link.experimentationConfiguration?.value}
                  >
                    {menuItem.label}
                  </Link>
                </li>
              );
            }
            return null;
          })}

          {globalNavLinks?.length ? (
            <li className="mt-8 px-4 pb-4">
              <HeaderLogoLink
                brand={{
                  code: 'WW',
                  url: '/',
                  name: 'Hilton For the Stay',
                }}
                variant={logoVariant}
              />
            </li>
          ) : null}
          {globalNavLinks?.map((menuItem, index) => {
            if (menuItem?.subMenu?.length) {
              return (
                <li key={menuItem.label}>
                  <button
                    ref={selectedItemIndex === index ? lastSelectedButton : null}
                    onClick={() => {
                      setSubmenuLogoBrand({
                        code: 'WW',
                        url: '/',
                        name: 'Hilton For the Stay',
                      });
                      itemSelected(menuItem, index);
                      dispatchGlobalNavigationClickBasedOnLabel(
                        menuItem.label,
                        undefined,
                        onGlobalNavigationClick
                      );
                    }}
                    className={cx(
                      'osc-nav-main-link osc-nav-drawer-link flex h-12 w-full animate-fadein items-center justify-between border-b border-surface ps-4 text-left'
                    )}
                    aria-expanded="false"
                    type="button"
                    data-conductrics-goal={menuItem.link?.experimentationConfiguration?.goal}
                    data-conductrics-value={menuItem.link?.experimentationConfiguration?.value}
                  >
                    <span>{menuItem.label}</span>
                    <Icon name="arrowhead-small-right" className="me-3 ms-auto rtl:rotate-180" />
                  </button>
                </li>
              );
            } else if (menuItem.link?.url) {
              return (
                <li key={menuItem.label}>
                  <Link
                    colorTheme="inherit"
                    anchorClassName="osc-nav-drawer-link block"
                    underline={false}
                    className={cx(
                      'osc-nav-main-link flex h-12 items-center border-b border-surface ps-4'
                    )}
                    url={replaceLang(menuItem.link.url, locale)}
                    isNewWindow={menuItem.link.isNewWindow}
                    onClick={(event) => {
                      dispatchGlobalNavigationClickBasedOnLabel(
                        menuItem.label,
                        undefined,
                        onGlobalNavigationClick
                      );
                      menuItem.onClick?.();
                      menuItem.link?.onClick?.(event);
                      sendGlobalNavLinkClickGoal();
                    }}
                    data-conductrics-goal={menuItem.link.experimentationConfiguration?.goal}
                    data-conductrics-value={menuItem.link.experimentationConfiguration?.value}
                  >
                    {menuItem.label}
                  </Link>
                </li>
              );
            }
            return null;
          })}
        </ul>
      )}
    </div>
  );
};
